import { useCallback, useRef } from 'react';

import { useGetCollectionMetadataByContract, useMultichainWS, useValidateFreshCollections } from '@pkg/hooks';
import { getFormattedChain } from '@pkg/hooks/tokens/useGetSlugChain';

import { type MappedYacsEvents, SupportedTokens, type YacsEventData, YacsMultichainEventTypes } from '@app/client';
import { type FreshCollection } from '@app/store/zustand';

interface GetFreshSalesProps {
    disabled?: boolean;
}
export function useGetFreshSales({ disabled = false }: GetFreshSalesProps) {
    const fetchCollectionMetadata = useGetCollectionMetadataByContract();
    const validateFreshCollections = useValidateFreshCollections();
    const contracts = useRef<string[]>([]);
    const unifiedContractAddresses = useRef<string[]>([]);

    const validateItems = useCallback(
        async (contractAddresses: string[]) => {
            fetchCollectionMetadata({ contractAddresses }).then(async (r) => {
                if (r.ok && r.value) {
                    const metadata = r.value;

                    for (const chain in metadata) {
                        const chainItems = metadata[chain];
                        const chainMeta = chainItems
                            ?.map((item) => {
                                if (item.metadata && (item.metadata.image || item.metadata.image_url)) {
                                    const chainFormatted = getFormattedChain(chain);
                                    return {
                                        ...item,
                                        metadata: {
                                            ...item.metadata,
                                            image_url: item.metadata.image_url || item.metadata.image,
                                        },
                                        slug: item?.slugs?.[0],
                                        chain: chainFormatted,
                                    } as FreshCollection;
                                }
                            })
                            .filter((item) => item !== undefined);

                        if (chainMeta?.length) {
                            await validateFreshCollections(chainMeta);
                        }
                    }
                }
            });
        },
        [fetchCollectionMetadata, validateFreshCollections],
    );

    const onSaleEvent = useCallback(
        async (eventData: YacsEventData) => {
            const saleEventData = eventData as MappedYacsEvents['saleEvent'];

            if (saleEventData) {
                const { contract, chain } = saleEventData;
                if (!contracts.current.includes(contract)) {
                    contracts.current = [...contracts.current, contract];
                    const unifiedContract = `${chain}.${contract}`;
                    if (!unifiedContractAddresses.current?.includes(unifiedContract)) {
                        unifiedContractAddresses.current = [...unifiedContractAddresses.current, unifiedContract];

                        if (unifiedContractAddresses?.current.length >= 5) {
                            await validateItems(unifiedContractAddresses?.current);
                            unifiedContractAddresses.current = [];
                        }
                    }
                }
            }
        },
        [validateItems],
    );

    useMultichainWS({
        eventType: YacsMultichainEventTypes.saleEvent,
        chain: SupportedTokens.Ethereum,
        onEvent: onSaleEvent,
        disabled,
    });
    useMultichainWS({
        eventType: YacsMultichainEventTypes.saleEvent,
        chain: SupportedTokens.Polygon,
        onEvent: onSaleEvent,
        disabled,
    });
}
